// Master-P3 Wave 2.3 — Werbekanäle (Ad-Platforms) tab.
//
// Rendered inside GuidedIntegrationCenter as the "Werbekanäle" tab
// (Wave 2 promotion from placeholder). Two sections:
//   * Betreiber-Config — platform-level env vars (boolean only).
//   * Kunden-Bereich — Meta + Google connect cards, capability chips
//     with declared/verified split, and clear callouts for
//     access-tier / app-review requirements.
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle }
  from "../../components/ui/alert";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  CheckCircle2, ShieldAlert, Link as LinkIcon, PlugZap,
  Settings2, Copy, RotateCcw,
} from "lucide-react";

const PLATFORMS = [
  { key: "meta", label: "Meta Ads (Facebook + Instagram)",
    api_note: "Marketing API v26.0",
    dev_console_url: "https://developers.facebook.com" },
  { key: "google", label: "Google Ads",
    api_note: "Google Ads API v25 (developer-token sunset 2026-09-09)",
    dev_console_url:
      "https://console.cloud.google.com/apis/library/googleads.googleapis.com" },
];

function CapabilityRow({ cap, platformKey }) {
  const declaredLabel = cap.declared ? "deklariert" : "nicht deklariert";
  const verifiedLabel = cap.verified === null ? "nicht geprüft"
    : cap.verified ? "verifiziert" : "verifiziert fehlgeschlagen";
  const verifiedTone = cap.verified === null ? "bg-muted text-muted-foreground"
    : cap.verified ? "bg-emerald-100 text-emerald-800"
    : "bg-red-100 text-red-800";
  return (
    <div className="flex items-center justify-between gap-3
                    border-t py-2 text-sm"
         data-testid={`ad-cap-row-${platformKey}-${cap.name}`}>
      <div className="flex-1">
        <div className="font-medium">{cap.name}</div>
        {cap.note && (
          <div className="text-xs text-muted-foreground mt-0.5">
            {cap.note}
          </div>
        )}
        {cap.requires_review && (
          <div className="text-xs text-amber-800 mt-0.5"
                data-testid={`ad-cap-review-${platformKey}-${cap.name}`}>
            Freigabe erforderlich: {cap.requires_review}
          </div>
        )}
      </div>
      <div className="flex gap-1 shrink-0">
        <Badge className="bg-sky-100 text-sky-800 border-0"
                data-testid={`ad-cap-declared-${platformKey}-${cap.name}`}>
          {declaredLabel}
        </Badge>
        <Badge className={`${verifiedTone} border-0`}
                data-testid={`ad-cap-verified-${platformKey}-${cap.name}`}>
          {verifiedLabel}
        </Badge>
      </div>
    </div>
  );
}

function ByoaPanel({ platform, onConnected }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/oauth/${platform.key}/settings`)
      .then((r) => {
        setState(r.data);
        if (!redirectUri) {
          setRedirectUri(r.data.expected_redirect_uri || "");
        }
      })
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [platform.key]); // eslint-disable-line

  const saveOwnApp = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/oauth/${platform.key}/settings`, {
        own_app_id: appId, own_app_secret: appSecret,
        own_redirect_uri: redirectUri || state?.expected_redirect_uri,
      });
      setState((s) => ({ ...(s || {}), ...r.data }));
      setShowForm(false); setAppSecret("");
      toast.success('BYOA konfiguriert. Klick jetzt „Verbinden“.');
      onConnected && onConnected();
    } catch (e) {
      toast.error(
        e.response?.data?.detail?.error || "byoa_upsert_rejected");
    } finally { setSaving(false); }
  };

  const resetToPlatformApp = async () => {
    try {
      const r = await api.delete(`/oauth/${platform.key}/settings`);
      setState((s) => ({ ...(s || {}), ...r.data }));
      toast.success("Zurück zur Plattform-App.");
    } catch (e) {
      toast.error("Reset fehlgeschlagen.");
    }
  };

  const copyRedirect = () => {
    if (!state?.expected_redirect_uri) return;
    navigator.clipboard?.writeText(state.expected_redirect_uri);
    toast.success("Redirect-URI kopiert.");
  };

  if (loading) return null;

  const platformAppAvailable = state?.platform_app_available;
  const byoaActive = state?.mode === "own_app" && state?.byoa_configured;

  return (
    <div className="mt-4 rounded-lg border p-4 space-y-4"
         data-testid={`byoa-panel-${platform.key}`}>
      <div className="grid gap-3 md:grid-cols-2">
        {/* Card A: Platform App */}
        <div className={`rounded border p-3 text-sm ${
              platformAppAvailable ? "" : "opacity-60"}`}
              data-testid={`byoa-card-platform-${platform.key}`}>
          <div className="font-medium flex items-center gap-2">
            <PlugZap className="h-4 w-4" />
            Plattform-Verbindung
            {platformAppAvailable ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-0">
                verfügbar
              </Badge>
            ) : (
              <Badge className="bg-muted text-muted-foreground border-0">
                in Vorbereitung
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {platformAppAvailable
              ? "Ein Klick — der Plattform-Betreiber übernimmt App-Registrierung + Review."
              : "Der Plattform-Betreiber richtet gerade die zentrale " +
                platform.label + "-Integration ein. Sobald verfügbar, verbindest du hier dein Konto mit einem Klick."}
          </p>
        </div>

        {/* Card B: BYOA */}
        <div className={`rounded border p-3 text-sm ${
              byoaActive ? "border-emerald-400 bg-emerald-50/30" : ""}`}
              data-testid={`byoa-card-own-${platform.key}`}>
          <div className="font-medium flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Eigene App verwenden
            {byoaActive && (
              <Badge className="bg-emerald-100 text-emerald-800 border-0">
                aktiv
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Empfohlen für das Test-Setup. Du registrierst selbst eine
            App bei {platform.label.split(" ")[0]} Developers und
            trägst Client-ID + Secret hier ein. Kein App-Review nötig.
          </p>
          {byoaActive ? (
            <div className="mt-3 space-y-2 text-xs">
              <div>App-ID: <code>{state.own_app_id}</code></div>
              <div>Secret: <code>••••{state.secret_last4}</code></div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm"
                        onClick={() => {
                          window.location.assign(
                            `/api/v1/oauth/${platform.key}/authorize`);
                        }}
                        data-testid={`btn-byoa-connect-${platform.key}`}>
                  <LinkIcon className="h-3 w-3 mr-1" />
                  Mit meiner App verbinden
                </Button>
                <Button size="sm" variant="secondary"
                        onClick={() => setShowForm(true)}
                        data-testid={`btn-byoa-edit-${platform.key}`}>
                  Ändern
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={resetToPlatformApp}
                        data-testid={`btn-byoa-reset-${platform.key}`}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Zur Plattform-App
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="secondary"
                    onClick={() => setShowForm(true)}
                    className="mt-3"
                    data-testid={`btn-byoa-open-${platform.key}`}>
              Eigene App konfigurieren →
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="rounded border p-4 space-y-3 bg-muted/30"
             data-testid={`byoa-form-${platform.key}`}>
          <div className="text-sm font-medium">
            Schritt für Schritt: Eigene {platform.label.split(" ")[0]}-App verbinden
          </div>
          <ol className="text-xs text-muted-foreground list-decimal pl-4 space-y-1">
            <li>Öffne <a target="_blank" rel="noreferrer"
                            href={platform.dev_console_url}
                            className="underline">
              {platform.dev_console_url}</a> und erstelle eine
              Business-App.</li>
            <li>Aktiviere die Produkte „Login for Business“ +
              „Marketing API“ (Meta) bzw. die Google/LinkedIn/TikTok
              Ads-API.</li>
            <li>Trage in deiner App unter „Gültige OAuth-Redirect-URIs“
              die folgende URL ein:
              <div className="flex items-center gap-2 mt-1">
                <code className="text-[10px] bg-background px-2 py-1
                                  rounded flex-1 break-all"
                       data-testid={`byoa-redirect-hint-${platform.key}`}>
                  {state?.expected_redirect_uri}
                </code>
                <Button size="sm" variant="ghost" onClick={copyRedirect}
                        data-testid={`btn-byoa-copy-${platform.key}`}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </li>
            <li>Kopiere App-ID + App-Secret unten und speichere.</li>
          </ol>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor={`app-id-${platform.key}`}>App-ID</Label>
              <Input id={`app-id-${platform.key}`}
                      value={appId} onChange={(e) => setAppId(e.target.value)}
                      placeholder={state?.own_app_id || "1234567890"}
                      data-testid={`byoa-input-app-id-${platform.key}`} />
            </div>
            <div>
              <Label htmlFor={`app-secret-${platform.key}`}>
                App-Secret
              </Label>
              <Input id={`app-secret-${platform.key}`} type="password"
                      value={appSecret}
                      onChange={(e) => setAppSecret(e.target.value)}
                      placeholder={state?.secret_last4
                        ? `••••${state.secret_last4}` : ""}
                      data-testid={`byoa-input-app-secret-${platform.key}`} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`redirect-${platform.key}`}>
                Redirect-URI (identisch zur in der App eingetragenen)
              </Label>
              <Input id={`redirect-${platform.key}`}
                      value={redirectUri}
                      onChange={(e) => setRedirectUri(e.target.value)}
                      data-testid={`byoa-input-redirect-${platform.key}`} />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost"
                    onClick={() => { setShowForm(false); setAppSecret(""); }}>
              Abbrechen
            </Button>
            <Button size="sm" onClick={saveOwnApp}
                    disabled={saving || !appId || !appSecret || !redirectUri}
                    data-testid={`btn-byoa-save-${platform.key}`}>
              {saving ? "Speichern…" : "Speichern & verbinden"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function PlatformCard({ platform, operatorState, onConnect }) {
  const [caps, setCaps] = useState(null);

  useEffect(() => {
    api.get(`/ad-platforms/${platform.key}/capabilities`)
      .then((r) => setCaps(r.data.capabilities || []))
      .catch(() => setCaps([]));
  }, [platform.key]);

  const configured = operatorState?.configured;

  return (
    <Card data-testid={`ad-platform-card-${platform.key}`}>
      <CardHeader className="flex flex-row items-start justify-between
                              gap-4 space-y-0">
        <div>
          <CardTitle>{platform.label}</CardTitle>
          <CardDescription className="mt-1">
            {platform.api_note}
            {operatorState?.note && (
              <div className="text-xs mt-1">{operatorState.note}</div>
            )}
          </CardDescription>
        </div>
        <Button size="sm"
                  disabled={!configured}
                  onClick={() => onConnect(platform.key)}
                  data-testid={`btn-connect-${platform.key}`}>
          <LinkIcon className="h-3 w-3 mr-1" />
          Verbinden
        </Button>
      </CardHeader>
      <CardContent>
        {!configured && (
          <Alert data-testid={`platform-not-configured-${platform.key}`}
                  className="mb-4">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Verbindungsoptionen</AlertTitle>
            <AlertDescription>
              Du hast zwei Wege, dein {platform.label.split(" ")[0]}-Werbekonto
              anzubinden: die Plattform-App (in Vorbereitung, ein Klick) oder
              deine eigene {platform.label.split(" ")[0]}-App (empfohlen für den
              Test-Setup, kein App-Review nötig).
            </AlertDescription>
          </Alert>
        )}
        <ByoaPanel platform={platform} onConnected={() => {}} />
        <div data-testid={`ad-capabilities-${platform.key}`}
              className="mt-4">
          {caps === null && (
            <p className="text-sm text-muted-foreground">
              Fähigkeiten werden geladen…
            </p>
          )}
          {caps && caps.map((c) => (
            <CapabilityRow key={c.name} cap={c}
                             platformKey={platform.key} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdChannelsTab() {
  const [operatorState, setOperatorState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ad-platforms/_operator-config-status")
      .then((r) => setOperatorState(r.data))
      .catch(() => setOperatorState({}))
      .finally(() => setLoading(false));
  }, []);

  const startOAuth = async (platform) => {
    try {
      const r = await api.post(
        `/ad-platforms/oauth/${platform}/start`, {});
      if (r.data.mock_mode) {
        toast.info("Preview-Modus: mock-callback wird aufgerufen.");
      }
      // Follow the returned URL. In preview this is our own
      // mock-callback; in production it would be the real provider.
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      toast.error(
        `Verbindung fehlgeschlagen: ${e.response?.data?.detail?.error
                                         || e.message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="ad-channels-tab">
      <div>
        <h2 className="text-xl font-semibold">Werbekanäle</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
          Verbinde Meta Ads und Google Ads. Betreiber-Konfiguration
          (App-Zugänge, OAuth-Clients) liegt platform-weit; jeder
          Tenant autorisiert seinen eigenen Account.
        </p>
      </div>

      <Card data-testid="operator-config-section">
        <CardHeader>
          <CardTitle className="text-base">
            Betreiber-Konfiguration
          </CardTitle>
          <CardDescription>
            Nur boolesche Anzeige — nie die eigentlichen Werte.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && <p className="text-sm">Wird geladen…</p>}
          {!loading && operatorState && PLATFORMS.map((p) => {
            const s = operatorState[p.key] || {};
            return (
              <div key={p.key}
                    className="flex items-center justify-between
                              border-t pt-2 text-sm"
                    data-testid={`operator-state-${p.key}`}>
                <div>
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs text-muted-foreground">
                    Ziel-API: {s.api_version_target || "?"}
                  </div>
                </div>
                {s.configured ? (
                  <Badge className="bg-emerald-100 text-emerald-800
                                      border-0"
                          data-testid={`operator-configured-${p.key}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                    konfiguriert
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-800 border-0"
                          data-testid={`operator-not-configured-${p.key}`}>
                    nicht konfiguriert
                  </Badge>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {PLATFORMS.map((p) => (
          <PlatformCard key={p.key} platform={p}
                          operatorState={operatorState?.[p.key]}
                          onConnect={startOAuth} />
        ))}
      </div>
    </div>
  );
}
